import React from 'react';

import { RelatedContentInfoContext } from '~commonInfra/telemetryEvents/contexts/TelemetryContexts';
import { Formatters, Messages } from '~src/codegen/Localize';
import { imgPath } from '~src/commonInfra/utils/ImgUrl';
import { FavoriteToggle } from '~src/favorites/components/favoriteToggle/FavoriteToggle';
import { CardTextAndLink } from '~src/spacesPlatform/components/card/CardTextAndLink';
import { CardViewStyles } from '~src/spacesPlatform/components/card/CardViewStyles';
import { ContentCard, getContentUrlForItem } from '~src/spacesPlatform/components/card/ContentCard';
import { DetailsTooltipStyles } from '~src/spacesPlatform/components/card/DetailsTooltipStyles';
import { Thumbnail } from '~src/spacesPlatform/components/card/Thumbnail';
import { TooltipAnchor } from '~src/spacesPlatform/components/tooltip/TooltipAnchor';
import { IRelatedContentInfo } from '~src/spacesPlatform/models/resourceInfoParts';
import { ViewTooltip } from '~src/views/components/ViewTooltip';
import { IViewInfo } from '~src/views/models/IViewInfo';

import { DataMonitoringExplanation, LongExplanation, PopularExplanation, ShortExplanation, SimiliarUsersExplanation } from '../../models/IViewRecommendationInfo';

export class RecommendationCard extends ContentCard {

  public render() {
    return (
      <div className={`${CardViewStyles.Card} ${CardViewStyles.RecommendationCard}`}>
        {this.renderRecommendationExplanation()}
        <TooltipAnchor tooltipContent={this.renderTooltipContent}
          width={DetailsTooltipStyles.Width}
          onShowTooltip={this.handleShowTooltip}
          title={Messages.viewTooltipTitle()}
          disableTooltip={this.props.disableTooltip}
        >
          <Thumbnail navigateUrl={getContentUrlForItem(this.props.content, this.props.urls) + this.getShowDataMonitoringParam()}
            defaultImageUrl={imgPath('spaces_card_view.svg')}
            thumbnailImageUrl={this.props.content.thumbnailUrl}
            hoveredOrSelected={this.props.hoveredOrSelected}
            allowDragAndDrop={this.props.allowDragAndDrop}
          />
        </TooltipAnchor>
        <div className={CardViewStyles.ActionRow} style={{marginBottom: '-3px'}}>
          <span data-tb-test-id="rec-item-name" className={CardViewStyles.ActionRowName} title={this.props.content.name}>
            {this.props.content.name}
          </span>
          {this.renderViewsCountForVizRec()}
          <span>
            <FavoriteToggle isTabbable={true}
              isFavorite={this.props.content.favorite}
              onAddFavorite={() => this.props.addContentFavorite({ ...this.props.content, contentType: 'view' })}
              onRemoveFavorite={() => this.props.removeContentFavorite({ ...this.props.content, contentType: 'view' })} />
          </span>
          {this.renderFavoritesCountForVizRec()}
          {this.renderActionsMenu()}
        </div>
        {this.renderParentWorkbookLink()}
      </div>
    );
  }

  private renderParentWorkbookLink = () => {
    const workbook = (this.props.content as IViewInfo).workbook;
    if (workbook) {
      const relatedInfo = {relatedContentId: workbook.id, relatedContentType: 'workbook'} as IRelatedContentInfo;
      return <div className={CardViewStyles.CardDescription}>
        <RelatedContentInfoContext.Provider value={relatedInfo} >
          <CardTextAndLink
            text={''}
            url={this.props.urls.workbookUrl(workbook.id)}
            urlDisplayName={workbook.name}
            marginLeft={-9}
            isRecommendationType={true}
          />
        </RelatedContentInfoContext.Provider>
      </div>;
    }
  };

  private renderViewsCountForVizRec = () => {
    const viewsCount = (this.props.content as IViewInfo).usageInfo.hitsTotal;
    if (Number.isInteger(viewsCount)) {
      return (
        <React.Fragment>
          <span className={CardViewStyles.VizRecViewsIcon} title={Messages.viewIconTooltip()}></span>
          <span className={CardViewStyles.VizRecViewsCount}>{Formatters.formatCountInRecommendationCard(viewsCount)}</span>
        </React.Fragment>
      );
    }
  };

  private renderFavoritesCountForVizRec = () => {
    const favoritesCount = (this.props.content as IViewInfo).usageInfo.favoritesTotal;
    if (Number.isInteger(favoritesCount)) {
      return <span className={CardViewStyles.VizRecFavoritesCount}>{Formatters.formatCountInRecommendationCard(favoritesCount)}</span>;
    }
  };

  private renderRecommendationExplanation = () => {
    let explanationShape: string = '';
    let explanationText = '';
    let textHoverClassName = CardViewStyles.RecommendationCardTextHover;
    let textClassName = CardViewStyles.RecommendationCardText;
    let shapeHoverClassName = CardViewStyles.RecommendationCardShapeHover;
    let shapeClassName = CardViewStyles.RecommendationCardShape;

    if (this.props.content.explanationType === PopularExplanation) {
      explanationText = Messages.vizRecommendationsPopularExplanation();
      if (explanationText.length <= ShortExplanation) {
        explanationShape = 'ribbon_Trending.svg';
      } else {
        explanationShape = 'ribbon_Trending_Long.svg';
        shapeClassName = CardViewStyles.RecommendationCardMidShape;
        textClassName = CardViewStyles.RecommendationCardLongText;
      }
    } else if (this.props.content.explanationType === SimiliarUsersExplanation) {
      explanationText = Messages.vizRecommendationsSimiliarUsersExplanation();
      if (explanationText.length <= ShortExplanation) {
        explanationShape = 'ribbon_ForYou.svg';
      } else {
        explanationShape = 'ribbon_ForYou_Long.svg';
        shapeClassName = CardViewStyles.RecommendationCardForYouShapeLong;
      }
    } else if (this.props.content.explanationType === DataMonitoringExplanation) {
      explanationText = Messages.vizRecommendationsDataChangeExplanation();
      if (explanationText.length <= LongExplanation) {
        explanationShape = 'ribbon_DataChange.svg';
        shapeClassName = CardViewStyles.RecommendationCardDCRShapeEn;
      } else {
        explanationShape = 'ribbon_DataChange_Long.svg';
        shapeClassName = CardViewStyles.RecommendationCardLongShape;
        textClassName = CardViewStyles.RecommendationCardLongText;
      }
    }

    if (explanationShape && explanationText) {
      /* eslint-disable jsx-a11y/alt-text */
      return <div data-tb-test-id="recommendation-explanation">
        <span className={this.props.hoveredOrSelected
          ? textHoverClassName
          : textClassName}>{explanationText}</span>
        <img src={imgPath(explanationShape)}
          className={this.props.hoveredOrSelected
            ? shapeHoverClassName
            : shapeClassName} />
      </div>;
    }
  };

  private renderTooltipContent = () => {
    return <ViewTooltip content={this.props.content}
      urls={this.props.urls} />;
  };

  private getShowDataMonitoringParam = () => {
    let showDataMonitoringParam: string = '';
    // Append the open uatu panel url param if the recommendation is from uatu
    if (this.props.content.explanationType && this.props.content.explanationType === DataMonitoringExplanation) {
      // User facing terminology is "Data Changes", and this string is user-facing
      showDataMonitoringParam = '?:showDataChanges=true';
    }
    return showDataMonitoringParam;
  };
}
